import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { getDefaultStore } from 'jotai';

import { asAPIError } from 'api/utils/APIError';
import { ErrorCode } from 'api/utils/ErrorCode';
import isModalError from 'api/utils/isModalError';
import { dispatchErrorModal } from 'store/errorModalEvent';
import { dispatchErrorToast } from 'store/errorToastEvent';
import { userAtom } from 'store';

function handleUnauthenticated() {
  getDefaultStore().set(userAtom, undefined);
}

const isLocalMockEnv = import.meta.env.VITE_APP_LOCAL_ENV === 'local';

function handleGlobalError(error: Error, meta?: Record<string, unknown>) {
  const apiError = asAPIError(error);
  if (apiError) {
    if (apiError.subCode === ErrorCode.UNAUTHENTICATED) {
      // In `start:mock`, an unmocked route bypasses MSW and the real API returns
      // UNAUTHENTICATED for the mock token — that must not wipe the local session.
      if (isLocalMockEnv) {
        console.warn(
          '[local mock] Ignoring UNAUTHENTICATED (likely an MSW miss). Session kept.',
          { message: apiError.message, statusCode: apiError.statusCode },
        );
        return;
      }
      handleUnauthenticated();
      return;
    }

    if (isModalError(error)) {
      dispatchErrorModal(error);
    } else if (meta?.errorToast) {
      dispatchErrorToast(error);
    }

    if (apiError.statusCode === 403) return;

    Sentry.captureException(error, {
      extra: {
        requestId: apiError.requestId,
        statusCode: apiError.statusCode,
        subCode: apiError.subCode,
      },
    });
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: import.meta.env.VITE_APP_DEPLOY_ENV === 'production' ? 3 : false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => handleGlobalError(error, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => handleGlobalError(error, mutation.meta),
  }),
});
